import { computed, nextTick, ref, toValue, watch, type MaybeRefOrGetter, type Ref } from 'vue'
import { useNuxtApp } from '#imports'

import { CurrencyAmount } from '~/utils/CurrencyAmount'
import { resolveDisplayLocale } from '~/utils/resolveDisplayLocale'

interface UseCurrencyInputOptions {
  readonly currency: MaybeRefOrGetter<string>
  readonly modelValue: MaybeRefOrGetter<number | string | null>
  readonly element: Ref<HTMLInputElement | null | undefined>
  readonly emitUpdate: (value: number | null) => void
}

/** Keeps a currency field's text, caret and numeric model in step: live grouping while focused, formatted currency otherwise. */
export function useCurrencyInput(options: UseCurrencyInputOptions) {
  const { $i18n } = useNuxtApp()
  const locale = computed(() => resolveDisplayLocale(String($i18n.locale.value)))
  const currency = computed(() => CurrencyAmount.supported(toValue(options.currency)))

  const isFocused = ref(false)
  const displayValue = ref('')

  const formatted = (value: number | string | null) =>
    CurrencyAmount.format(value, currency.value, locale.value)

  interface PendingEdit {
    readonly inputType: string
    readonly start: number
    readonly end: number
    readonly value: string
  }

  const NO_EDIT: PendingEdit = { end: 0, inputType: '', start: 0, value: '' }
  let pendingEdit: PendingEdit = NO_EDIT

  const onBeforeInput = (event: Event): void => {
    const element = options.element.value
    pendingEdit =
      event instanceof InputEvent && element
        ? {
            end: element.selectionEnd ?? 0,
            inputType: event.inputType,
            start: element.selectionStart ?? 0,
            value: element.value
          }
        : NO_EDIT
  }

  const isCollapsedSelection = (edit: PendingEdit): boolean => edit.start === edit.end

  // The character a collapsed deletion removed, from the text before the edit.
  const removedCharacter = (edit: PendingEdit): string => {
    if (!isCollapsedSelection(edit)) return ''
    if (edit.inputType === 'deleteContentBackward') return edit.value.charAt(edit.start - 1)
    if (edit.inputType === 'deleteContentForward') return edit.value.charAt(edit.start)
    return ''
  }

  const hasRemovedGroupSeparator = (edit: PendingEdit): boolean =>
    removedCharacter(edit) === CurrencyAmount.separators(currency.value, locale.value).group

  // A deletion that removed a grouping separator is re-grouped unchanged; it deletes the adjacent digit instead.
  function crossSeparator(
    raw: string,
    caret: number | null
  ): { raw: string; caret: number | null } {
    const edit = pendingEdit
    const hasEditToCross = caret !== null && hasRemovedGroupSeparator(edit)
    if (!hasEditToCross) return { caret, raw }
    const canDeleteBackward = edit.inputType === 'deleteContentBackward' && caret > 0
    const canDeleteForward = edit.inputType === 'deleteContentForward' && caret < raw.length
    if (canDeleteBackward) {
      return { caret: caret - 1, raw: `${raw.slice(0, caret - 1)}${raw.slice(caret)}` }
    }
    if (canDeleteForward) {
      return { caret, raw: `${raw.slice(0, caret)}${raw.slice(caret + 1)}` }
    }
    return { caret, raw }
  }

  function onInput(typed: string): void {
    const element = options.element.value
    const { caret, raw } = crossSeparator(typed, element?.selectionStart ?? null)
    pendingEdit = NO_EDIT
    const entry = CurrencyAmount.sanitize(raw, currency.value, locale.value)
    const value = entry.hasAmount ? entry.value : null
    const visible = isFocused.value ? entry.text : formatted(value)

    options.emitUpdate(value)
    displayValue.value = visible
    if (!element || element.value === visible) return

    element.value = visible
    if (isFocused.value && caret !== null) {
      const count = CurrencyAmount.significantCount(
        raw.slice(0, caret),
        currency.value,
        locale.value
      )
      const position = CurrencyAmount.caretAfter(visible, count, currency.value, locale.value)
      element.setSelectionRange(position, position)
    }
  }

  function onFocus(): void {
    isFocused.value = true
    displayValue.value = CurrencyAmount.edit(
      toValue(options.modelValue),
      currency.value,
      locale.value
    )
    // The text swap collapses the browser's focus selection; reselect so typing replaces the value.
    void nextTick(() => options.element.value?.select())
  }

  function onBlur(): void {
    isFocused.value = false
    displayValue.value = formatted(toValue(options.modelValue))
  }

  watch(
    [() => toValue(options.modelValue), currency, locale],
    ([value]) => {
      if (!isFocused.value) displayValue.value = formatted(value)
    },
    { immediate: true }
  )

  return { displayValue, onBeforeInput, onBlur, onFocus, onInput }
}
